// ADR 15.10.26d §0 — field reports, latched on delivery.
//
// `installStrip.ts`'s shape (25.9.26g §6), for Sentry rather than PostHog:
// Sentry is constructed behind `import()` after the first render, and only
// once the learner has granted consent (6.9.26c), so a report fired while the
// shell mounts meets no instance. It is parked here instead of lost, and
// `flushFieldReports` — chained behind `initTelemetry` in `main.tsx` — is the
// one retry. A tab that denied consent settles with nothing attached: the
// queue stays parked, nothing is latched, and the device is evaluated afresh
// on its next load.
//
// A report is **data** so a suite can assert on it without an SDK, and an
// optional latch key is written only once the report has landed: a device
// that never delivered is never marked as having reported.

import { attached, reportCaught, reportNonFatal } from '../capture'

export interface FieldReport {
  kind: string
  /** `caught` carries the real error (`reportCaught`); `nonfatal` builds a synthetic one. */
  transport: 'caught' | 'nonfatal'
  error?: unknown
  fingerprint: string[]
  tags: Record<string, string>
  contexts: Record<string, Record<string, unknown>>
  level?: 'info' | 'warning'
}

/** Where a report goes, and **whether it landed** — `true` writes the latch. */
export type FieldSink = (report: FieldReport) => boolean

export type LatchStorage = Pick<Storage, 'getItem' | 'setItem'>

/** The production sink: `capture.ts`, then `attached()` to tell a no-op from a delivery. */
const defaultSink: FieldSink = (report) => {
  const { fingerprint, tags, contexts, level } = report
  if (report.transport === 'caught') {
    reportCaught(report.error, level === undefined ? { fingerprint, tags, contexts } : { fingerprint, tags, contexts, level })
  } else {
    reportNonFatal(report.kind, fingerprint, tags, contexts)
  }
  return attached().crashReports
}

let sink: FieldSink = defaultSink

/** Test seam: watch the reports without attaching an SDK. */
export function setFieldSink(replacement: FieldSink): void {
  sink = replacement
}

/** Put `capture.ts` back — the production path. */
export function resetFieldSink(): void {
  sink = defaultSink
}

/** The park queue's bound: a boot that fires more than this keeps the newest. */
export const FIELD_QUEUE_LIMIT = 8

interface Parked {
  report: FieldReport
  latchKey?: string
  storage?: LatchStorage
}

let parked: Parked[] = []

/** Test-only: drop the parked reports. */
export function resetFieldDeliveryForTests(): void {
  parked = []
}

/** `localStorage` when it exists; a throwing getter (a sandboxed frame) is "none". */
function defaultStorage(): LatchStorage | undefined {
  try {
    return globalThis.localStorage ?? undefined
  } catch {
    return undefined
  }
}

/** Whether `latchKey` was written by an earlier delivery. A throwing storage reads as "not latched". */
export function latched(latchKey: string, storage: Pick<Storage, 'getItem'> | undefined = defaultStorage()): boolean {
  try {
    return storage?.getItem(latchKey) != null
  } catch {
    return false
  }
}

function writeLatch(latchKey: string | undefined, storage: LatchStorage | undefined): void {
  if (latchKey === undefined) return
  try {
    storage?.setItem(latchKey, '1')
  } catch {
    // A throwing storage still sent; it is merely unlatched.
  }
}

function park(entry: Parked): void {
  parked.push(entry)
  if (parked.length > FIELD_QUEUE_LIMIT) parked.shift()
}

function attempt(entry: Parked): void {
  let landed = false
  try {
    landed = sink(entry.report)
  } catch {
    landed = false
  }
  if (landed) writeLatch(entry.latchKey, entry.storage)
  else park(entry)
}

/**
 * Send a report; latch `latchKey` only if it landed, else park it. An
 * already-latched key sends nothing. Never throws.
 */
export function deliver(report: FieldReport, latchKey?: string, storage: LatchStorage | undefined = defaultStorage()): void {
  if (latchKey !== undefined && latched(latchKey, storage)) return
  attempt({ report, latchKey, storage })
}

/** The retry, chained after `initTelemetry` in `main.tsx`. A report that still cannot land is parked again. */
export function flushFieldReports(): void {
  const waiting = parked
  parked = []
  for (const entry of waiting) {
    if (entry.latchKey !== undefined && latched(entry.latchKey, entry.storage)) continue
    attempt(entry)
  }
}
